import { useCallback, useSyncExternalStore } from 'react'

/**
 * Live reasoning ("thinking") text, published straight from the stream
 * accumulators and keyed by assistant message id.
 *
 * The message tree's comparators deliberately drop per-token thinking renders
 * — a reasoning delta must not re-render a whole turn's subtree — which left an
 * expanded "Reasoning" row stale until the stream ended, then completing in a
 * burst. The row subscribes here instead, so it streams as a quarantined leaf
 * while the rest of the turn stays memoized.
 *
 * Deliberately not a Jotai atom (`components.mdc` §6): a per-token atom write
 * ticks every subscriber of the chat store, which is the render storm this
 * exists to avoid. Subscription is per message id so only the row that owns
 * the text wakes up.
 *
 * This mirrors text the messages atom already holds; it is a render channel,
 * not a source of truth. Entries are bounded, and evicting one is safe: the
 * stream-end `isChatLoading` flip re-renders the turn with final props, so a
 * settled row falls back to its prop text unchanged.
 */

export const MAX_TRACKED_THINKING_MESSAGES = 32

const thinkingText = new Map<string, string>()
const listeners = new Map<string, Set<() => void>>()

function evictOldest() {
  while (thinkingText.size > MAX_TRACKED_THINKING_MESSAGES) {
    const oldest = thinkingText.keys().next().value
    if (oldest === undefined) {
      return
    }
    thinkingText.delete(oldest)
  }
}

export function publishThinkingText(messageId: string, text: string): void {
  if (!messageId) {
    return
  }
  if (thinkingText.get(messageId) === text) {
    return
  }

  // Re-insert so insertion order tracks last write — an actively streaming
  // message is never the eviction candidate.
  thinkingText.delete(messageId)
  thinkingText.set(messageId, text)
  evictOldest()

  const messageListeners = listeners.get(messageId)
  if (!messageListeners) {
    return
  }
  for (const listener of messageListeners) {
    listener()
  }
}

export function getThinkingText(messageId: string | undefined): string | undefined {
  return messageId ? thinkingText.get(messageId) : undefined
}

export function subscribeToThinkingText(messageId: string, onStoreChange: () => void): () => void {
  let messageListeners = listeners.get(messageId)
  if (!messageListeners) {
    messageListeners = new Set()
    listeners.set(messageId, messageListeners)
  }
  messageListeners.add(onStoreChange)

  return () => {
    messageListeners.delete(onStoreChange)
    if (messageListeners.size === 0) {
      listeners.delete(messageId)
    }
  }
}

const noopSubscribe = () => () => {}

/**
 * Live reasoning text for a message, falling back to whatever the render tree
 * last handed down when the store has nothing for it (history, or a message
 * whose entry has been evicted).
 */
export function useLiveThinkingText(messageId: string | undefined, fallback: string): string {
  const subscribe = useCallback(
    (onStoreChange: () => void) => (messageId ? subscribeToThinkingText(messageId, onStoreChange) : noopSubscribe()),
    [messageId],
  )
  const getSnapshot = useCallback(() => getThinkingText(messageId), [messageId])

  return useSyncExternalStore(subscribe, getSnapshot) ?? fallback
}

export function __resetThinkingStreamForTests(): void {
  thinkingText.clear()
  listeners.clear()
}
